import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import MonthlyDonationHero from "./MonthlyDonationHero";
import YourImpact from "./YourImpact";
import WhatYouDone from "./WhatYouDone";
import DonateMonthly from "./DonateMonthly";
import HelpUs from "./HelpUs";
import Updates from "./Updates";
import Faq from "../../components/common/CampaignDetailPageComponents/Faq";
import TestimonialSection from "./TestimonialSection";
import DonationBox from "../../components/common/DonationBox";
import { Box, useMediaQuery, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchMonthlyDonationByIdAction, fetchMonthlyDonationsAction } from "../../store/slices/monthlyDonationSlice";
import { getMonthlyDonations } from "../../api/services/monthlyDonationService";

const Index = () => {
  const isMobile = useMediaQuery('(max-width: 600px)');
  const [searchParams] = useSearchParams();
  const donationId = searchParams.get('id');
  const dispatch = useDispatch();
  const { byId, loading, list } = useSelector((state) => state.monthlyDonation);
  const [donationData, setDonationData] = useState(null);
  const [fetching, setFetching] = useState(false);
  const hasFetchedRef = useRef(false); // Prevent multiple fetches
  const currentDonationIdRef = useRef(null); // Track current donation ID
  // Amount from donation box so "Donate Monthly Now" buttons can pass it
  const [donationAmount, setDonationAmount] = useState(3000);

  useEffect(() => {
    // Reset if donationId changes
    if (currentDonationIdRef.current !== donationId) {
      hasFetchedRef.current = false;
      currentDonationIdRef.current = donationId;
      setDonationData(null);
    }

    if (!donationId || hasFetchedRef.current) {
      return;
    }

    // First check if we already have it in store
    if (byId[donationId]) {
      setDonationData(byId[donationId]);
      hasFetchedRef.current = true;
      return;
    }

    // Check if it's in the list (from originalData)
    const donationFromList = list.find(item => {
      const originalId = item.originalData?.id || item.id;
      return String(originalId) === String(donationId);
    });
    
    if (donationFromList?.originalData) {
      setDonationData(donationFromList.originalData);
      // Also store it in byId for future use
      dispatch({
        type: 'monthlyDonation/fetchMonthlyDonationById/fulfilled',
        payload: { data: donationFromList.originalData }
      });
      hasFetchedRef.current = true;
      return;
    }

    // Prevent multiple simultaneous requests
    if (fetching) {
      return;
    }

    // Try to fetch by ID first
    hasFetchedRef.current = true;
    setFetching(true);
    dispatch(fetchMonthlyDonationByIdAction(donationId))
      .then((result) => {
        if (result.type === 'monthlyDonation/fetchMonthlyDonationById/fulfilled' && result.payload?.data) {
          setDonationData(result.payload.data);
        } else {
          // If by-id endpoint fails, fetch all and find by ID
          return getMonthlyDonations(true); // Include raw data
        }
      })
      .then((allDonationsResponse) => {
        if (!allDonationsResponse) return;
        
        // Check raw data first
        if (allDonationsResponse?.rawData) {
          const found = allDonationsResponse.rawData.find(item => String(item.id) === String(donationId));
          if (found) {
            setDonationData(found);
            dispatch({
              type: 'monthlyDonation/fetchMonthlyDonationById/fulfilled',
              payload: { data: found }
            });
            return;
          }
        }
        // Fallback to transformed data
        if (allDonationsResponse?.data) {
          const found = allDonationsResponse.data.find(item => {
            const originalId = item.originalData?.id || item.id;
            return String(originalId) === String(donationId);
          });
          if (found?.originalData) {
            setDonationData(found.originalData);
            dispatch({
              type: 'monthlyDonation/fetchMonthlyDonationById/fulfilled',
              payload: { data: found.originalData }
            });
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching monthly donation:', error);
        // Only retry once if not already fetched
        if (!donationData) {
          getMonthlyDonations(true).then((allDonationsResponse) => {
            if (allDonationsResponse?.rawData) {
              const found = allDonationsResponse.rawData.find(item => String(item.id) === String(donationId));
              if (found) {
                setDonationData(found);
              }
            } else if (allDonationsResponse?.data) {
              const found = allDonationsResponse.data.find(item => {
                const originalId = item.originalData?.id || item.id;
                return String(originalId) === String(donationId);
              });
              if (found?.originalData) {
                setDonationData(found.originalData);
              }
            }
          }).catch((err) => {
            console.error('Fallback fetch also failed:', err);
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }, [donationId, dispatch]); // Removed byId and list from dependencies

  // Separate effect to update data when Redux store updates (but don't trigger fetch)
  useEffect(() => {
    if (donationId && byId[donationId] && !donationData) {
      setDonationData(byId[donationId]);
    }
  }, [donationId, byId, donationData]);

  if ((loading || fetching) && !donationData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!donationData && !loading && !fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ fontSize: '18px', color: '#7D7D7D', mb: 2 }}>
            Monthly donation not found
          </Box>
          {donationId && (
            <Box sx={{ fontSize: '14px', color: '#999', mt: 1 }}>
              ID: {donationId}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
  
  const hasHeroData =
    donationData?.banner_image ||
    donationData?.monthly_banner_title ||
    donationData?.monthly_banner_subtitle ||
    (donationData?.monthly_media_uploads?.length > 0);
  const hasImpactImages =
    donationData?.monthly_image_carousel?.length > 0;
  const hasImpactDescriptions =
    donationData?.impactDescriptions?.length > 0;
  const hasDonateMonthlyImage = !!donationData?.monthly_reason_image;

  return (
    <>
      {hasHeroData && (
        <MonthlyDonationHero donationData={donationData} donationAmount={donationAmount} />
      )}
      {hasImpactImages && <YourImpact donationData={donationData} />}
      {hasImpactDescriptions && (
        <Box sx={{ width: "100%", maxWidth: "1440px", mx: "auto" }}>
          <WhatYouDone donationData={donationData} />
        </Box>
      )}
      {hasDonateMonthlyImage && (
        <Box sx={{ width: "100%", maxWidth: "1440px", mx: "auto" }}>
          <DonateMonthly donationData={donationData} />
        </Box>
      )}
      {(donationData?.updates?.length > 0 || donationData?.monthly_media_uploads?.length > 0) && (
        <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto" }}>
          <Updates donationData={donationData} />
        </Box>
      )}
      <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto" }}>
        <HelpUs donersImage={donationData?.doners_image} donationData={donationData} />
      </Box>
      <Box sx={{ width: "100%", maxWidth: "1440px", mx: "auto" }}>
        <TestimonialSection donationData={donationData} donationAmount={donationAmount} />
      </Box>
      <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto" }}>
        <Faq />
      </Box>

      {/* Sticky Donation Box */}
      <Box
        data-donation-box="true"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          padding: isMobile ? "0px" : "10px 0",
        }}
      >
        <DonationBox 
          campaignTitle={donationData?.title || ""} 
          campaignId={donationData?.id || null}
          isMonthlyDonation={true}
          onDonationAmountChange={setDonationAmount}
        />
      </Box>
    </>
  );
};

export default Index;